import type * as CC from 'vanilla-cookieconsent';

/** Optional categories. `necessary` is always present and cannot be switched off. */
export type OptionalCategory = 'functional' | 'analytics' | 'marketing';
export type CategoryName = 'necessary' | OptionalCategory;

export const OPTIONAL_CATEGORIES: readonly OptionalCategory[] = ['functional', 'analytics', 'marketing'];

/** Languages shipped with the package. Any other language can be added through `translations`. */
export type { BuiltInLanguage } from './translations/languages';

import type { TrackerId } from './trackers';

export type ServiceKey = TrackerId;

/** What the visitor did, as recorded in the consent log. */
export type ConsentAction = 'accept_all' | 'reject_all' | 'custom';

export interface ConsentState {
  consentId: string;
  revision: number;
  accepted: CategoryName[];
  rejected: CategoryName[];
  action: ConsentAction;
  language: string;
  timestamp: string;
}

export interface DVConsentConfig {
  /** Site identifier, sent with every consent log record. */
  siteId: string;
  /** Data controller shown in the banner (ZEKom-2, 225/1 and GDPR art. 13 require it). */
  controller: { name: string };
  /** URL of the cookie / privacy policy, linked from the banner. */
  policyUrl: string;
  /** Languages to enable. Default: ['sl', 'en']. The first is the fallback. */
  languages?: string[];
  /** Where to detect the language from. Default: 'document' (the <html lang> attribute). */
  autoDetectLanguage?: 'document' | 'browser';
  /**
   * Services in use. Optional: with `autoBlock` (default) Kockica detects known trackers on the page itself.
   * List services here only for trackers it cannot see before the banner opens (e.g. loaded much later).
   */
  services?: ServiceKey[];
  /** Detect and block known trackers automatically until consent. Default: true. */
  autoBlock?: boolean;
  /**
   * Google tags (GA4, Google Ads): 'advanced' (default) loads them and lets Consent Mode v2 keep them cookieless
   * until consent; 'basic' blocks them until consent like any other tracker.
   */
  googleMode?: 'advanced' | 'basic';
  /** Extra optional categories to show even without a preset (e.g. your own scripts). */
  categories?: OptionalCategory[];
  /** Send Google Consent Mode v2 signals (default + update). Default: true. */
  consentMode?: boolean;
  /** HTTPS endpoint that receives consent records (POST, JSON). Omit to disable logging. */
  logEndpoint?: string;
  /** Bump to ask everyone again (e.g. after adding a new purpose). Default: 0. */
  revision?: number;
  /** How long a choice is kept, in days. Default: 182 (about 6 months). */
  expiresAfterDays?: number;
  /** Cookie domain, e.g. '.example.com' to share consent across subdomains. */
  cookieDomain?: string;
  /** Consent modal layout. Default: 'box'. */
  layout?: 'box' | 'bar';
  /** Consent modal position. Default: 'bottom left' for box, 'bottom' for bar. */
  position?: string;
  /**
   * Show the small floating button for changing consent (Data Vallis cubes). Default: true.
   * With false, give visitors another always-visible way, e.g. a footer link with `data-kockica-preferences`.
   */
  revisitButton?: boolean;
  /** Corner of the floating button. Default: 'left'. */
  revisitPosition?: 'left' | 'right';
  /** Show the "Kockica · Data Vallis" credit in the banner footer. Default: true. */
  branding?: boolean;
  /** Honour the browser's Global Privacy Control signal. Default: false. */
  respectGpc?: boolean;
  /** Override or add texts per language. Merged over the built-in texts. */
  translations?: Record<string, DeepPartial<CC.Translation>>;
  /** Called after the first choice and every change. */
  onChange?: (state: ConsentState) => void;
}

export type DeepPartial<T> = { [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K] };

export interface DVConsentApi {
  acceptedCategory: (name: CategoryName) => boolean;
  acceptedService: (service: ServiceKey) => boolean;
  showPreferences: () => void;
  show: () => void;
  getState: () => ConsentState | null;
  /** Forget the visitor's choice and show the banner again. */
  reset: () => void;
}
